/**
 * AssistantPanel — the unified page-aware assistant, rendered as a floating
 * card anchored to whichever corner the launcher puck (`AssistantLauncher`)
 * is currently pinned to, opening inward so it clears the puck. Page content
 * stays visible/interactive so capabilities actions land on what the user is
 * looking at.
 *
 * Two views: the chat (default) with an "On this page" capabilities section,
 * and the org-shared thread list (assistant conversations are visible to the
 * whole org; the active one is pinned per browser session).
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { EXCLUDE_ATTR } from '@runcofactor/guide';
import {
  assistantPageLabel,
  isAssistantPageId,
  ONBOARDING_KICKOFF_MARKER_KIND,
  THREAD_KICKOFF_MARKER_KIND,
} from '@chatsdet/types';
import { EDGE_MARGIN, PUCK_SIZE, type Corner, type Point, type Rect } from './cornerPin';
import { LogoMarkPhysics } from './LogoMarkPhysics';
import type { PendingImpulse } from './discPhysics';
import {
  Button,
  ChatMessage,
  ChatMessageList,
  ChatComposer,
  Markdown,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  StatusBadge,
  TagBadge,
  Text,
  ToolCall,
  Tooltip,
  TooltipTrigger,
  cn,
  groupToolCallParts,
  summarizeToolInput,
} from '@chatsdet/ui';
import {
  CircleNotch,
  ChatsCircle,
  CaretDown,
  CaretRight,
  CheckCircle,
  CloudArrowUp,
  BookBookmark,
  ClipboardText,
  LinkSimple,
  Paperclip,
  Plus,
  Sparkle,
  WarningCircle,
  X,
  ArrowLeft,
  Archive,
} from '@phosphor-icons/react';
import { mapToolState } from '../../hooks/chat';
import { useAssistantThreads } from './useAssistantThreads';
import { usePageCapabilities } from './usePageCapabilities';
import { useAssistantDocuments } from './useAssistantDocuments';
import { FirstRunWelcome } from './FirstRunWelcome';
import { useContextDocuments, type ContextDocument } from '../context-library/useContextLibrary';
import { fileTypeIcon } from '../context-library/fileTypeMeta';
import {
  useAssistantActivities,
  type AssistantActivityOutcome,
  type AutobuildStage,
} from './useAssistantActivities';
import type { useAssistantChat, AssistantUIMessage } from './useAssistantChat';

/** Accepted file types for direct upload — mirrors `ContextUploadZone`'s list. */
const FILE_ACCEPT =
  'image/*,application/pdf,text/plain,.txt,text/markdown,.md,text/csv,.csv,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx';

// -----------------------------------------------------------------------------
// Tool display
// -----------------------------------------------------------------------------

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  capabilities: 'Using this page',
  read_guide: 'Reading a guide',
  search_docs: 'Searching docs',
  save_memory: 'Remembering',
  search_memory: 'Recalling',
  list_memories: 'Reviewing memories',
  forget_memory: 'Forgetting',
  read_context_document: 'Reading a document',
};

const QUIET_TOOLS = new Set([
  'read_guide',
  'save_memory',
  'search_memory',
  'list_memories',
  'forget_memory',
]);

type ToolPartLike = {
  type: string;
  toolName?: string;
  state?: string;
  errorText?: string;
  input?: unknown;
  output?: unknown;
};

/** The signed image of a successful `browser_session` screenshot, or null. */
function browserScreenshotImageUrl(part: ToolPartLike): string | null {
  const toolName = part.toolName ?? part.type.replace(/^tool-/, '');
  if (part.type !== 'tool-browser_session' && toolName !== 'browser_session') return null;
  const input = part.input as { action?: string } | undefined;
  if (input?.action !== 'screenshot') return null;
  const output = part.output as { ok?: boolean; imageUrl?: string } | undefined;
  if (output?.ok !== true) return null;
  return typeof output.imageUrl === 'string' ? output.imageUrl : null;
}

function renderToolPart(part: ToolPartLike, key: string): React.ReactNode {
  const toolName = part.toolName ?? part.type.replace(/^tool-/, '');
  const state = mapToolState(part.state ?? 'output-available');

  if (toolName === 'navigate') {
    // Labelled from the INPUT, never the output: client-executed tool results
    // are not persisted (a reloaded thread shows the part with its input in
    // both slots, as `page_ui` already does), so an output-based label would
    // regress to the pending text the moment the thread refetches.
    const input = (part.input ?? {}) as { pageId?: string; reason?: string };
    const pageId = input.pageId ?? '';
    const label = isAssistantPageId(pageId) ? assistantPageLabel(pageId) : 'another page';
    // The model's `reason` is promised to the user in the tool's own
    // description; this chip is the one place it shows.
    const reason = input.reason ? ` · ${input.reason}` : '';
    const displayName = `${state === 'success' ? 'Went to' : 'Going to'} ${label}${reason}`;
    return (
      <ToolCall
        key={key}
        toolName={toolName}
        displayName={displayName}
        state={state}
        error={part.errorText}
        grouped
        hideDetails
      />
    );
  }

  if (toolName === 'capabilities') {
    const input = (part.input ?? {}) as {
      action?: string;
      capabilityId?: string;
      args?: Record<string, unknown>;
    };
    const displayName =
      input.action === 'call'
        ? `Using ${input.capabilityId ?? 'a capability'}`
        : input.action === 'get'
          ? `Inspecting ${input.capabilityId ?? 'a capability'}`
          : 'Checking page capabilities';
    return (
      <ToolCall
        key={key}
        toolName={toolName}
        displayName={displayName}
        state={state}
        input={input.args}
        output={part.output}
        error={part.errorText}
        inputSummary={input.args ? summarizeToolInput(input.args) : undefined}
        grouped
      />
    );
  }

  if (toolName === 'browser_session') {
    const input = (part.input ?? {}) as {
      action?: string;
      url?: string;
      code?: string;
      label?: string;
      caption?: string;
    };
    const output = part.output as
      | {
          ok: true;
          text: string;
          storageKey?: string;
          mediaId?: string;
          imageUrl?: string;
          status?: 'starting';
        }
      | { ok: false; error: string; hint?: string }
      | undefined;

    // The SDK's own state only distinguishes pending/streaming from
    // output-available — an `{ ok: false }` payload still arrives as
    // output-available, so it must be reclassified as an error here.
    const toolState = output && output.ok === false ? 'error' : state;
    const errorMessage = output && output.ok === false ? output.error : part.errorText;

    let displayName: string;
    switch (input.action) {
      case 'start':
        displayName =
          toolState === 'error'
            ? "Couldn't open the browser"
            : toolState === 'success'
              ? output && output.ok && output.status === 'starting'
                ? 'Still opening the browser…'
                : 'Browser open'
              : 'Opening a browser…';
        break;
      case 'goto':
        displayName = `${toolState === 'success' ? 'Went to' : 'Going to'} ${input.url ?? 'a page'}`;
        break;
      case 'run_code':
        displayName = input.label ?? 'Interacting with the page';
        break;
      case 'snapshot':
        displayName = 'Reading the page';
        break;
      case 'screenshot':
        displayName = input.caption ?? 'Taking a screenshot';
        break;
      case 'end':
        displayName = toolState === 'success' ? 'Browser closed' : 'Closing the browser';
        break;
      default:
        displayName = toolName.replace(/_/g, ' ');
    }

    const imageUrl = browserScreenshotImageUrl(part);

    if (imageUrl) {
      return (
        <Stack key={key} gap="xs">
          <ToolCall
            toolName={toolName}
            displayName={displayName}
            state={toolState}
            error={errorMessage}
            grouped
            hideDetails
          />
          <ScreenshotPreview src={imageUrl} alt={input.caption ?? 'Screenshot'} />
        </Stack>
      );
    }

    return (
      <ToolCall
        key={key}
        toolName={toolName}
        displayName={displayName}
        state={toolState}
        error={errorMessage}
        grouped
        hideDetails
      />
    );
  }

  return (
    <ToolCall
      key={key}
      toolName={toolName}
      displayName={TOOL_DISPLAY_NAMES[toolName] ?? toolName.replace(/_/g, ' ')}
      state={state}
      error={part.errorText}
      grouped
      hideDetails={QUIET_TOOLS.has(toolName)}
      input={QUIET_TOOLS.has(toolName) ? undefined : part.input}
      output={QUIET_TOOLS.has(toolName) ? undefined : part.output}
    />
  );
}

/**
 * Screenshot preview for the `browser_session` tool. Signed URLs expire
 * after ~1 hour, so a load failure (revisiting an old thread) swaps in a
 * muted placeholder rather than a broken image.
 */
const ScreenshotPreview: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [expired, setExpired] = React.useState(false);

  if (expired) {
    return <p className="px-2 py-1 text-xs text-muted-foreground italic">Screenshot expired</p>;
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setExpired(true)}
      className="max-w-full rounded-md border border-border"
    />
  );
};

// -----------------------------------------------------------------------------
// Navigation divider
// -----------------------------------------------------------------------------

/**
 * System messages carrying a `metadata.kind` are markers, not chat turns:
 * navigation hints and activity reports today. They render as dividers.
 *
 * Matched by the PRESENCE of a kind rather than an allowlist, mirroring the
 * server's own marker rule in `assistant/service.ts` — an allowlist here would
 * mean a new marker kind reaches the model but renders as nothing.
 *
 * The onboarding kickoff (and the plain thread kickoff, `THREAD_KICKOFF_
 * MARKER_KIND`) are the one exception: they're real USER turns (the model
 * needs to see them as one), so they can't be a `system` message like every
 * other marker — matched on `metadata.kind` alone, regardless of role.
 */
function isMarkerMessage(message: AssistantUIMessage): boolean {
  const metadata = message.metadata as { kind?: string } | undefined;
  if (metadata?.kind === ONBOARDING_KICKOFF_MARKER_KIND) return true;
  if (metadata?.kind === THREAD_KICKOFF_MARKER_KIND) return true;
  return message.role === 'system' && typeof metadata?.kind === 'string';
}

function messageText(message: AssistantUIMessage): string {
  return message.parts
    .map((part) => ('text' in part && typeof part.text === 'string' ? part.text : ''))
    .join('');
}

/**
 * Divider label for a marker message. The kickoff hides its actual (hidden)
 * user turn behind a fixed label rather than showing the raw prompt text. A
 * thread kickoff carries its own `label` in metadata (set by the page that
 * requested it); the onboarding kickoff uses a fixed label instead.
 */
function markerLabel(message: AssistantUIMessage): string {
  const metadata = message.metadata as { kind?: string; label?: string } | undefined;
  if (metadata?.kind === ONBOARDING_KICKOFF_MARKER_KIND) return 'Onboarding started';
  if (metadata?.kind === THREAD_KICKOFF_MARKER_KIND) return metadata.label ?? 'Thread started';
  return messageText(message);
}

/** Icon for a marker's divider, keyed by `metadata.kind`. None for unknown kinds. */
function markerIcon(message: AssistantUIMessage): React.ReactNode {
  const metadata = message.metadata as { kind?: string } | undefined;
  if (metadata?.kind === 'context-attached') {
    return <Paperclip size={11} className="shrink-0" />;
  }
  if (metadata?.kind === ONBOARDING_KICKOFF_MARKER_KIND || metadata?.kind === THREAD_KICKOFF_MARKER_KIND) {
    return <Sparkle size={11} className="shrink-0" />;
  }
  return null;
}

/**
 * Divider for a marker message. The label truncates: navigation markers are
 * short ("Navigated to Flows list"), but an activity marker carries a flow
 * name and an outcome and overflows a 400px panel. Full text on hover.
 */
const MarkerDivider: React.FC<{ label: string; icon?: React.ReactNode }> = ({ label, icon }) => (
  <div className="flex items-center gap-2 px-3 py-1 text-[11px] text-muted-foreground/70">
    <div className="h-px flex-1 shrink bg-border" />
    {icon}
    <span className="min-w-0 truncate" title={label}>
      {label}
    </span>
    <div className="h-px flex-1 shrink bg-border" />
  </div>
);

// -----------------------------------------------------------------------------
// On this page
// -----------------------------------------------------------------------------

const OnThisPageSection: React.FC<{
  pageId: Parameters<typeof usePageCapabilities>[0];
  pageLabel: string;
  panelOpen: boolean;
}> = ({ pageId, pageLabel, panelOpen }) => {
  const [expanded, setExpanded] = React.useState(false);
  const capabilitiesQuery = usePageCapabilities(pageId, panelOpen);
  const data = capabilitiesQuery.data;

  if (pageId === 'unknown' || !data || data.capabilities.length === 0) {
    return null;
  }

  const pageCapabilities = data.capabilities.filter((capability) => !capability.global);
  if (pageCapabilities.length === 0) return null;
  const implementedCount = pageCapabilities.filter((c) => c.status === 'implemented').length;

  return (
    <div className="shrink-0 border-b border-border bg-muted/30">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <span className="font-medium">On this page · {pageLabel}</span>
        <span className="ml-auto tabular-nums">
          {implementedCount}/{pageCapabilities.length}
        </span>
      </button>
      {expanded && (
        <ul className="max-h-48 overflow-y-auto px-3 pb-2">
          {pageCapabilities.map((capability) => (
            <li key={capability.id} className="flex items-start gap-2 py-1 text-xs">
              <span
                className={cn(
                  'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                  capability.status === 'implemented' ? 'bg-primary' : 'bg-muted-foreground/40'
                )}
              />
              <div className="min-w-0">
                <span className="text-foreground">{capability.title}</span>
                {capability.status === 'planned' && (
                  <TagBadge name="Planned" color="gray" size="sm" className="ml-1.5" />
                )}
                <div className="text-muted-foreground line-clamp-2">{capability.description}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Activity strip
// -----------------------------------------------------------------------------

const STAGE_LABELS: Record<AutobuildStage, string> = {
  spec_review: 'Reviewing spec',
  authoring: 'Authoring',
  converging: 'Caching',
  verifying: 'Verifying',
  completed: 'Finishing up',
};

const OUTCOME_LABELS: Record<AssistantActivityOutcome, string> = {
  converged: 'Converged',
  unverified: 'Unverified',
  blocked: 'Blocked',
  error: 'Error',
  not_testable: 'Not testable',
  spec_review_failed: 'Spec review failed',
  canceled: 'Canceled',
  partially_converged: 'Partially converged',
  awaiting_review: 'Awaiting review',
};

const ACTIVITY_KIND_LABELS = {
  flow_autobuild: 'Autobuild',
  flow_maintenance: 'Maintenance',
} as const;

const ActivitySection: React.FC<{ conversationId: string | null; panelOpen: boolean }> = ({
  conversationId,
  panelOpen,
}) => {
  const { activities } = useAssistantActivities({ conversationId, enabled: panelOpen });

  if (activities.length === 0) return null;

  return (
    <div className="shrink-0 border-b border-border bg-muted/30">
      <ul className="px-3 py-2">
        {activities.map((activity) => {
          const activityKindLabel = ACTIVITY_KIND_LABELS[activity.kind];
          const statusLabel =
            activity.status === 'running'
              ? activity.kind === 'flow_maintenance'
                ? 'Maintenance in progress'
                : activity.stage
                  ? STAGE_LABELS[activity.stage]
                  : 'Working'
              : activity.outcome
                ? OUTCOME_LABELS[activity.outcome]
                : 'Finished';
          return (
            <li key={activity.id}>
              <Link
                to="/flows/$workflowId"
                params={{ workflowId: activity.workflowId }}
                // Title and status are rendered as separate truncated nodes, so
                // without this a screen reader gets no single name tying them.
                aria-label={`${activityKindLabel}: ${activity.title} — ${statusLabel}`}
                className="flex items-center gap-2 py-1 text-xs hover:text-foreground transition-colors"
              >
                {activity.status === 'running' ? (
                  <CircleNotch size={12} className="shrink-0 animate-spin text-muted-foreground" />
                ) : null}
                <span className="min-w-0 flex-1 truncate text-foreground" title={activity.title}>
                  {activity.title}
                </span>
                <TagBadge name={activityKindLabel} color="gray" size="sm" hideDot />
                {activity.status === 'running' ? (
                  <span className="shrink-0 text-muted-foreground">{statusLabel}</span>
                ) : (
                  activity.outcome && (
                    <StatusBadge
                      status={
                        activity.outcome === 'converged'
                          ? 'success'
                          : activity.outcome === 'partially_converged' ||
                              activity.outcome === 'awaiting_review'
                            ? 'warning'
                            : 'failed'
                      }
                      label={OUTCOME_LABELS[activity.outcome]}
                      size="sm"
                    />
                  )
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Composer attachments
// -----------------------------------------------------------------------------

/** Small status glyph for a document's extraction state, next to its chip. */
const DocStatusIcon: React.FC<{ status: ContextDocument['status'] }> = ({ status }) => {
  switch (status) {
    case 'pending':
    case 'processing':
      return <CircleNotch size={12} className="shrink-0 animate-spin text-muted-foreground" />;
    case 'ready':
      return <CheckCircle size={12} className="shrink-0 text-primary" />;
    case 'failed':
      return <WarningCircle size={12} className="shrink-0 text-destructive" />;
    default:
      return null;
  }
};

/**
 * One chip per document attached to the thread — icon, truncated name,
 * extraction status, and a remove button. Wraps across lines since the panel
 * is only 400px wide.
 */
const AttachmentChips: React.FC<{
  documents: ContextDocument[];
  onRemove: (documentId: string) => void;
}> = ({ documents, onRemove }) => {
  if (documents.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-1 pb-2">
      {documents.map((doc) => {
        const Icon = fileTypeIcon(doc.fileType);
        return (
          <span
            key={doc.id}
            className="group inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/50 py-1 pl-2 pr-1.5 text-xs text-foreground"
          >
            <Icon size={13} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 max-w-[9rem] truncate" title={doc.fileName}>
              {doc.fileName}
            </span>
            <DocStatusIcon status={doc.status} />
            <button
              type="button"
              onClick={() => onRemove(doc.id)}
              aria-label={`Remove ${doc.fileName}`}
              className="shrink-0 rounded p-0.5 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
            >
              <X size={11} />
            </button>
          </span>
        );
      })}
    </div>
  );
};

/**
 * Compact popover listing the whole org context library, so a document
 * already on file elsewhere can be pulled into this thread without
 * re-uploading it. Documents already attached here show as disabled
 * "Attached" rows rather than being offered twice.
 */
const LibraryPickerButton: React.FC<{
  attachedIds: Set<string>;
  onAttach: (documentId: string) => void;
  isDisabled?: boolean;
}> = ({ attachedIds, onAttach, isDisabled }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="top start">
      <PopoverTrigger>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Add from library"
          aria-label="Add from library"
          isDisabled={isDisabled}
        >
          <BookBookmark size={13} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        {/* The list is a child so its query only runs while the popover is
            open. Mounted alongside the toolbar it would fetch — and, while
            anything in the org is still extracting, poll every 4s — for every
            user with the panel open, whether or not they ever open it. */}
        <LibraryPickerList attachedIds={attachedIds} onAttach={onAttach} />
      </PopoverContent>
    </Popover>
  );
};

const LibraryPickerList: React.FC<{
  attachedIds: Set<string>;
  onAttach: (documentId: string) => void;
}> = ({ attachedIds, onAttach }) => {
  const libraryQuery = useContextDocuments();
  const docs = libraryQuery.data ?? [];

  return (
    <>
      <Text className="px-1 pb-1 text-xs font-medium text-muted-foreground">Context library</Text>
      {libraryQuery.isLoading ? (
        <div className="flex items-center gap-2 px-1 py-2 text-xs text-muted-foreground">
          <CircleNotch size={12} className="animate-spin" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <Text className="px-1 py-2 text-xs text-muted-foreground">
          No documents in the library yet.
        </Text>
      ) : (
        <ul className="max-h-64 overflow-y-auto">
          {docs.map((doc) => {
            const attached = attachedIds.has(doc.id);
            const Icon = fileTypeIcon(doc.fileType);
            return (
              <li
                key={doc.id}
                className="flex items-center gap-2 rounded px-1 py-1.5 text-xs hover:bg-muted/50"
              >
                <Icon size={13} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate" title={doc.fileName}>
                  {doc.fileName}
                </span>
                <Button
                  variant={attached ? 'ghost' : 'secondary'}
                  size="sm"
                  isDisabled={attached}
                  onPress={() => onAttach(doc.id)}
                >
                  {attached ? 'Attached' : 'Attach'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

/**
 * Composer toolbar: attach file / add link / pull from library. Icon-only —
 * the panel is 400px wide, so labels live in tooltips instead.
 */
const ComposerToolbar: React.FC<{
  isBusy: boolean;
  attachedIds: Set<string>;
  onUploadFiles: (files: File[]) => void;
  onAddLink: (url: string) => void;
  onPasteFromClipboard: () => void;
  onAttachFromLibrary: (documentId: string) => void;
}> = ({
  isBusy,
  attachedIds,
  onUploadFiles,
  onAddLink,
  onPasteFromClipboard,
  onAttachFromLibrary,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (files.length > 0) onUploadFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [onUploadFiles]
  );

  const submitLink = React.useCallback(() => {
    const url = linkUrl.trim();
    if (!url) return;
    onAddLink(url);
    setLinkUrl('');
    setShowLinkInput(false);
  }, [linkUrl, onAddLink]);

  if (showLinkInput) {
    return (
      <input
        type="url"
        autoFocus
        value={linkUrl}
        placeholder="https://…"
        onChange={(event) => setLinkUrl(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submitLink();
          } else if (event.key === 'Escape') {
            setShowLinkInput(false);
            setLinkUrl('');
          }
        }}
        onBlur={() => {
          if (!linkUrl.trim()) setShowLinkInput(false);
        }}
        className="h-6 min-w-0 flex-1 rounded border border-border bg-background px-1.5 text-xs text-foreground outline-none focus:border-primary"
      />
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={FILE_ACCEPT}
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Attach file"
        aria-label="Attach file"
        isDisabled={isBusy}
        onPress={() => fileInputRef.current?.click()}
      >
        <Paperclip size={13} />
      </Button>
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Add link"
        aria-label="Add link"
        isDisabled={isBusy}
        onPress={() => setShowLinkInput(true)}
      >
        <LinkSimple size={13} />
      </Button>
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Paste text as a document"
        aria-label="Paste text as a document"
        isDisabled={isBusy}
        onPress={onPasteFromClipboard}
      >
        <ClipboardText size={13} />
      </Button>
      <LibraryPickerButton
        attachedIds={attachedIds}
        onAttach={onAttachFromLibrary}
        isDisabled={isBusy}
      />
    </div>
  );
};

// -----------------------------------------------------------------------------
// Thread list
// -----------------------------------------------------------------------------

const ThreadList: React.FC<{
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onBack: () => void;
}> = ({ activeConversationId, onSelect, onBack }) => {
  const [showArchived, setShowArchived] = React.useState(false);
  const { threads, isLoading, archiveThread } = useAssistantThreads({
    enabled: true,
    includeArchived: showArchived,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={12} /> Back to chat
        </button>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          Archived
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
            <CircleNotch size={12} className="animate-spin" /> Loading threads…
          </div>
        ) : threads.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            No shared assistant chats yet.
          </div>
        ) : (
          threads.map((conversation) => (
            <div
              key={conversation.id}
              className={cn(
                'group flex w-full items-start gap-2 border-b border-border/50 px-3 py-2 text-left hover:bg-muted/40 transition-colors',
                conversation.id === activeConversationId && 'bg-muted/60'
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(conversation.id)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="truncate text-xs font-medium text-foreground">
                  {conversation.title ?? 'Assistant chat'}
                </div>
                {conversation.latestMessage?.content && (
                  <div className="truncate text-[11px] text-muted-foreground">
                    {conversation.latestMessage.content}
                  </div>
                )}
              </button>
              {conversation.status === 'active' && (
                <TooltipTrigger>
                  <button
                    type="button"
                    onClick={() => archiveThread(conversation.id)}
                    className="p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                    aria-label="Archive thread"
                  >
                    <Archive size={13} />
                  </button>
                  <Tooltip>Archive</Tooltip>
                </TooltipTrigger>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Panel
// -----------------------------------------------------------------------------

/** One geometric state of the captive-takeover surface: its rect and corner radius. */
export type TakeoverBox = { rect: Rect; borderRadius: number };
/** Like `TakeoverBox`, but `borderRadius` may be a keyframe sequence — the expansion's `[9999, 28, 0]`. */
export type TakeoverAnimateBox = { rect: Rect; borderRadius: number | number[] };

/** A single property's spring-free tween — duration/easing/(optional) keyframe `times`. */
export type TakeoverPropTransition = {
  duration: number;
  ease?: [number, number, number, number];
  times?: number[];
};

/**
 * The center-mark morph: the puck's own `LogoMarkPhysics` mark, re-rendered
 * (a second, at-rest instance — see `MarkMorph` below) inside the takeover
 * surface at the SAME fixed point the puck occupied, so the surface's circle
 * reads as the puck itself rather than something that replaced it. Positioned
 * `fixed` at a frozen viewport point (not tied to the surface's own growing
 * box) so it never stretches; only its own opacity/scale animate.
 *
 * Re-mounts fresh for the expansion vs. the collapse (`AssistantHost.tsx`
 * renders `mark: null` in between, at `open`/`finishing`) — each needs its
 * OWN `initial`, which framer only honors at mount.
 */
export type TakeoverMark = {
  /** Top-left of the 44px mark box, in fixed viewport coordinates. */
  top: number;
  left: number;
  initialOpacity: number;
  initialScale: number;
  opacity: number;
  scale: number;
  transition: { duration: number; delay?: number; ease?: 'easeOut' };
};

/**
 * Drives the captive-takeover render — everything about WHERE and HOW BIG the
 * surface is lives here, computed by `AssistantHost.tsx`'s phase machine
 * (`onboardingFlow.ts`'s `TakeoverPhase`). `AssistantPanel` itself has no
 * opinion on phases; it just tweens `initial` → `animate` (mount only) and
 * `animate` (already-mounted target changes) exactly like any other
 * `motion.div`.
 */
export type TakeoverGeometry = {
  title: string;
  /** The wrapper's mount-only starting box (framer ignores this after mount). */
  initial: TakeoverBox;
  /** The current target box — changing this on an already-mounted instance is what tweens it. */
  animate: TakeoverAnimateBox;
  transition: Record<'top' | 'left' | 'width' | 'height' | 'borderRadius', TakeoverPropTransition>;
  /** Non-null while the center-mark morph should be on screen (expanding/collapsing only). */
  mark: TakeoverMark | null;
  /** `'shown'`/`'hidden'` variant name for the staggered header→list→composer reveal, below. */
  contentVariant: 'shown' | 'hidden';
  /** Fires once `animate`'s rect/radius tween completes. */
  onAnimationComplete?: () => void;
};

/**
 * Staggered reveal for the takeover's header/list/composer — replaces a flat
 * opacity fade so the content reads as arriving in sequence rather than all
 * at once. `delayChildren` on `shown` is what makes the header start ~60% of
 * the way through the surface's own expansion (`AssistantHost.tsx` times the
 * two against the same expansion duration). `staggerDirection: -1` on
 * `hidden` reverses the order on the way out (composer first, header last),
 * finishing well before the surface itself starts to shrink.
 */
const CONTENT_CONTAINER_VARIANTS = {
  hidden: { transition: { staggerChildren: 0.05, staggerDirection: -1 as const } },
  shown: { transition: { staggerChildren: 0.06, delayChildren: 0.27 } },
};
const HEADER_VARIANTS = {
  hidden: { opacity: 0, y: -6 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};
const LIST_VARIANTS = {
  hidden: { opacity: 0, y: 8 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.22 } },
};
const COMPOSER_VARIANTS = {
  hidden: { opacity: 0, y: 16 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.24, ease: 'easeOut' as const } },
};

/**
 * A second, independent `LogoMarkPhysics` instance for the center-mark morph
 * — deliberately inert (never dragged, no impulses queued), which settles at
 * rest after its first frame and stays there. Reuses the SAME component and
 * geometry as the puck rather than a hand-drawn copy, so the two can never
 * visually drift apart.
 */
const MarkMorph: React.FC<{ mark: TakeoverMark }> = ({ mark }) => {
  const accelRef = React.useRef<Point>({ x: 0, y: 0 });
  const activeRef = React.useRef(false);
  const impulseRef = React.useRef<PendingImpulse | null>(null);
  return (
    <motion.div
      style={{ position: 'fixed', top: mark.top, left: mark.left, width: 44, height: 44, zIndex: 41 }}
      initial={{ opacity: mark.initialOpacity, scale: mark.initialScale }}
      animate={{ opacity: mark.opacity, scale: mark.scale }}
      transition={mark.transition}
    >
      <LogoMarkPhysics
        size={44}
        accelRef={accelRef}
        activeRef={activeRef}
        impulseRef={impulseRef}
        runToken={0}
      />
    </motion.div>
  );
};

export type AssistantPanelProps = {
  isOpen: boolean;
  chat: ReturnType<typeof useAssistantChat>;
  onClose: () => void;
  /** Corner the launcher puck is pinned to — the ordinary card opens inward from here. */
  corner: Corner;
  /**
   * Overrides the header's "New chat" action (default: `chat.startNewChat`).
   * The takeover render uses this to also collapse back to the corner.
   */
  onNewChat?: () => void;
  /**
   * Set while the captive-takeover choreography owns the surface. Replaces
   * the ordinary corner-anchored render entirely — geometry, no border/shadow,
   * page background — with the SAME header/list/composer body.
   */
  takeover?: TakeoverGeometry | null;
  /** New org (no applications or tests): the empty thread shows the first-run welcome. */
  isFirstRun: boolean;
};

/** Gap between the launcher puck and the floating card, in px. */
const CARD_GAP = 12;
/** Distance from the screen edge to the card, clearing the puck + gap. */
const CARD_OFFSET = EDGE_MARGIN + PUCK_SIZE + CARD_GAP;

function cardPositionStyle(corner: Corner): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (corner === 'top-left' || corner === 'bottom-left') {
    style.left = EDGE_MARGIN;
  } else {
    style.right = EDGE_MARGIN;
  }
  if (corner === 'top-left' || corner === 'top-right') {
    style.top = CARD_OFFSET;
  } else {
    style.bottom = CARD_OFFSET;
  }
  return style;
}

function cardTransformOrigin(corner: Corner): string {
  const vertical = corner.startsWith('top') ? 'top' : 'bottom';
  const horizontal = corner.endsWith('left') ? 'left' : 'right';
  return `${vertical} ${horizontal}`;
}

export function AssistantPanel({
  isOpen,
  chat,
  onClose,
  corner,
  onNewChat,
  takeover = null,
  isFirstRun,
}: AssistantPanelProps) {
  const [view, setView] = React.useState<'chat' | 'threads'>('chat');
  const reducedMotion = useReducedMotion();

  // AnimatePresence below owns mount/unmount so the card can animate out.
  const {
    pageContext,
    conversationId,
    messages,
    chatInput,
    setChatInput,
    isSending,
    isPreparing,
    handleSendMessage,
    handleStopChat,
    switchConversation,
    startNewChat,
    announceAttachment,
  } = chat;

  const documents = useAssistantDocuments(conversationId);
  const attachedIds = React.useMemo(
    () => new Set(documents.documents.map((doc) => doc.id)),
    [documents.documents]
  );
  const isAttaching =
    documents.upload.isPending ||
    documents.addLink.isPending ||
    documents.addPaste.isPending ||
    documents.attachFromLibrary.isPending;

  const uploadFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      documents.upload.mutate(files, {
        onSuccess: (docs) => docs.forEach((doc) => announceAttachment(doc.fileName)),
      });
    },
    [documents.upload, announceAttachment]
  );
  const addLink = React.useCallback(
    (url: string) => {
      documents.addLink.mutate({ url }, { onSuccess: (doc) => announceAttachment(doc.fileName) });
    },
    [documents.addLink, announceAttachment]
  );
  // Reads the clipboard rather than opening a textarea: the panel is 400px
  // wide and the user has just copied the thing they want to attach. Matches
  // the flow planner's paste affordance.
  const pasteFromClipboard = React.useCallback(() => {
    void navigator.clipboard
      .readText()
      .then((text) => {
        const content = text.trim();
        if (!content) return;
        documents.addPaste.mutate(
          { content },
          { onSuccess: (doc) => announceAttachment(doc.fileName) }
        );
      })
      .catch(() => {
        // Clipboard read is permission-gated and the browser has already told
        // the user why. Attaching a file or a link still works.
      });
  }, [documents.addPaste, announceAttachment]);
  const attachFromLibrary = React.useCallback(
    (documentId: string) => {
      documents.attachFromLibrary.mutate(documentId, {
        onSuccess: (doc) => announceAttachment(doc.fileName),
      });
    },
    [documents.attachFromLibrary, announceAttachment]
  );
  const detachDocument = React.useCallback(
    (documentId: string) => documents.detach.mutate(documentId),
    [documents.detach]
  );

  // Drag-and-drop is scoped to the panel card, not the document/window: the
  // assistant drives the page via `page_ui` and the underlying page must stay
  // interactive by design, so a full-page drop capture would hijack drops
  // meant for the app underneath. The depth counter prevents bubbling
  // dragenter/dragleave across children must not flicker the overlay.
  const [isDragging, setIsDragging] = React.useState(false);
  const dragCounterRef = React.useRef(0);

  const handleDragEnter = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    if (event.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = React.useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      uploadFiles(Array.from(event.dataTransfer.files));
    },
    [uploadFiles]
  );

  const enterY = corner.startsWith('bottom') ? 8 : -8;

  // Shared between the ordinary corner render and the takeover render —
  // neither the message list nor the composer forks between the two modes.
  const body = (
    <>
      {isDragging && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-primary bg-primary/5 backdrop-blur-sm">
          <CloudArrowUp size={40} className="text-primary" />
          <Text size="sm" weight="medium" className="text-primary">
            Drop files to attach
          </Text>
        </div>
      )}

      {/* Header — rounded to match the card's top corners. `variants` only
          animates when an ancestor sets `animate` to a matching variant NAME
          (the takeover's content container, below) — inert in corner mode,
          where the ancestor's `animate` is a plain geometry object. */}
      <motion.div
        variants={HEADER_VARIANTS}
        className="flex h-16 shrink-0 items-center justify-between border-b px-4"
      >
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-medium text-foreground">Assistant</span>
          <TagBadge
            name={takeover ? takeover.title : pageContext.label}
            color="purple"
            size="sm"
            icon={<Sparkle size={10} weight="fill" />}
          />
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Shared chats"
            aria-label="Threads"
            aria-pressed={view === 'threads'}
            onPress={() => setView(view === 'threads' ? 'chat' : 'threads')}
            className={view === 'threads' ? 'text-foreground' : undefined}
          >
            <ChatsCircle size={16} />
          </Button>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="New chat"
            aria-label="New chat"
            onPress={() => {
              (onNewChat ?? startNewChat)();
              setView('chat');
            }}
          >
            <Plus size={16} />
          </Button>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Close"
            aria-label="Close"
            onPress={onClose}
          >
            <X size={16} />
          </Button>
        </div>
      </motion.div>

      {view === 'threads' ? (
        <ThreadList
          activeConversationId={conversationId}
          onSelect={(id) => {
            switchConversation(id);
            setView('chat');
          }}
          onBack={() => setView('chat')}
        />
      ) : (
        <motion.div variants={LIST_VARIANTS} className="flex min-h-0 flex-1 flex-col">
          <OnThisPageSection
            pageId={pageContext.pageId}
            pageLabel={pageContext.label}
            panelOpen={isOpen}
          />
          <ActivitySection conversationId={conversationId} panelOpen={isOpen} />

          <ChatMessageList className="min-h-0 flex-1 px-2" isStreaming={isSending}>
            {messages.length === 0 ? (
              // In takeover mode this empty state must never render: it's
              // only ever on screen for the handful of frames between the
              // surface opening and the kickoff turn landing in `messages`
              // (mints its conversation, then persists+streams the hidden
              // kickoff) — long enough to flicker "Opening your chat…" or the
              // ordinary prompt copy, never long enough to read either. The
              // staggered reveal and the assistant's own first line are the
              // only things that should appear.
              takeover ? null : isFirstRun && !isPreparing ? (
                <div className="h-full overflow-y-auto">
                  <FirstRunWelcome />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
                  {isPreparing ? (
                    <span className="flex items-center gap-2">
                      <CircleNotch size={14} className="animate-spin" /> Opening your chat…
                    </span>
                  ) : (
                    <>
                      Ask about this page or tell me what to do — I can act on what you're looking
                      at, search the docs, and remember what matters.
                    </>
                  )}
                </div>
              )
            ) : (
              messages.map((message) => {
                if (isMarkerMessage(message)) {
                  return (
                    <MarkerDivider
                      key={message.id}
                      label={markerLabel(message)}
                      icon={markerIcon(message)}
                    />
                  );
                }
                if (message.role === 'system') return null;

                const taggedParts: Array<{
                  key: string;
                  node: React.ReactNode;
                  isTool: boolean;
                }> = [];

                message.parts.forEach((part, i) => {
                  if (
                    part.type === 'text' &&
                    'text' in part &&
                    typeof part.text === 'string' &&
                    part.text.trim()
                  ) {
                    taggedParts.push({
                      key: `text-${i}`,
                      isTool: false,
                      node: (
                        <Markdown
                          key={`text-${i}`}
                          className="prose prose-sm max-w-none text-foreground"
                          compact
                        >
                          {part.text}
                        </Markdown>
                      ),
                    });
                    return;
                  }
                  if (part.type.startsWith('tool-')) {
                    // A screenshot the assistant took is content the user asked to
                    // see, not a tool row to fold away — `groupToolCallParts`
                    // collapses consecutive tool parts into one row showing only
                    // the last call, which would hide the inline image behind a
                    // click. Render it standalone so it always shows and splits
                    // the surrounding group instead of absorbing into it.
                    const toolPart = part as unknown as ToolPartLike;
                    taggedParts.push({
                      key: `tool-${i}`,
                      isTool: browserScreenshotImageUrl(toolPart) === null,
                      node: renderToolPart(toolPart, `tool-${i}`),
                    });
                  }
                });

                const renderedParts = groupToolCallParts(taggedParts);
                if (renderedParts.length === 0) {
                  if (message.role !== 'assistant') return null;
                  renderedParts.push(
                    <div key="thinking" className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CircleNotch size={12} className="animate-spin" />
                      <span>Working…</span>
                    </div>
                  );
                }

                return (
                  <ChatMessage key={message.id} role={message.role} className="px-3">
                    <Stack gap="sm">{renderedParts}</Stack>
                  </ChatMessage>
                );
              })
            )}
          </ChatMessageList>
        </motion.div>
      )}

      {view !== 'threads' && (
        <motion.div variants={COMPOSER_VARIANTS} className="shrink-0 border-t border-border bg-card p-1">
          {isPreparing && messages.length > 0 ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <CircleNotch size={12} className="animate-spin" />
              <Text size="xs" className="text-muted-foreground">
                Syncing the conversation…
              </Text>
            </div>
          ) : null}
          <ChatComposer
            value={chatInput}
            onChange={setChatInput}
            onSubmit={handleSendMessage}
            onStop={handleStopChat}
            isSending={isSending}
            submitLabel="Send"
            stopLabel="Stop"
            placeholder={takeover ? 'Reply…' : `Ask about ${pageContext.label.toLowerCase()}…`}
            className="border-none"
            attachments={<AttachmentChips documents={documents.documents} onRemove={detachDocument} />}
            actions={
              <ComposerToolbar
                isBusy={isAttaching}
                attachedIds={attachedIds}
                onUploadFiles={uploadFiles}
                onAddLink={addLink}
                onPasteFromClipboard={pasteFromClipboard}
                onAttachFromLibrary={attachFromLibrary}
              />
            }
          />
        </motion.div>
      )}
    </>
  );

  // The assistant must never be able to click itself closed — its own
  // portal root is off-limits to the guide, not just unclickable.
  const excludeProp = { [EXCLUDE_ATTR]: true };

  const panel = takeover ? (
    <motion.div
      key="assistant-takeover"
      {...excludeProp}
      initial={{
        top: takeover.initial.rect.top,
        left: takeover.initial.rect.left,
        width: takeover.initial.rect.width,
        height: takeover.initial.rect.height,
        borderRadius: takeover.initial.borderRadius,
      }}
      animate={{
        top: takeover.animate.rect.top,
        left: takeover.animate.rect.left,
        width: takeover.animate.rect.width,
        height: takeover.animate.rect.height,
        borderRadius: takeover.animate.borderRadius,
      }}
      transition={takeover.transition}
      onAnimationComplete={takeover.onAnimationComplete}
      // No border, no shadow: the page background reads as the page itself
      // having become the conversation, not a panel floating above it.
      className="fixed z-40 flex flex-col overflow-hidden bg-background"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <motion.div
        variants={CONTENT_CONTAINER_VARIANTS}
        initial="hidden"
        animate={takeover.contentVariant}
        className="flex min-h-0 flex-1 flex-col"
      >
        {body}
      </motion.div>
    </motion.div>
  ) : (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="assistant-panel"
          {...excludeProp}
          initial={reducedMotion ? undefined : { opacity: 0, scale: 0.96, y: enterY }}
          animate={reducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
          // Same enterY, not its negation: the exit is the entrance played backwards.
          exit={reducedMotion ? undefined : { opacity: 0, scale: 0.96, y: enterY }}
          transition={reducedMotion ? undefined : { duration: 0.16 }}
          style={{ ...cardPositionStyle(corner), transformOrigin: cardTransformOrigin(corner) }}
          className={cn(
            'fixed z-40',
            'w-[400px] h-[min(600px,calc(100vh-8rem))] flex flex-col',
            'rounded-lg border border-border bg-background shadow-2xl overflow-hidden'
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {body}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(
    <>
      {panel}
      {takeover?.mark && <MarkMorph mark={takeover.mark} />}
    </>,
    document.body
  );
}
